import { useEffect, useState, lazy, Suspense } from 'react';
import { Capacitor } from '@capacitor/core';
import { LandingPage } from './components/landing/LandingPage';
import { DownloadToPlayGate } from './components/landing/DownloadToPlayGate';
import { FullScreenLoader } from './components/ui/LoadingSpinner';
import { isWebPlayAllowed, isEchidnaWebAccessAllowed } from './lib/native/webPlayAccess';
import { DemoModeProvider } from './contexts/DemoModeContext';
import { supabase } from './lib/supabase/client';
import { isMobileLike } from './lib/native/mobileMirror';
import { getBrand } from './lib/branding';
const EchidnaLoader = lazy(() =>
  import('./components/echidna/EchidnaLoader').then((m) => ({ default: m.EchidnaLoader })),
);

// Echidna — the second title (variant) on the shared engine. Lazy-loaded
// so its game-logic + dictionary stay out of the PokerBlitz main bundle.
// Reachable at /echidna on web for play-testing; the eventual standalone
// variant build sets VITE_VARIANT_KEY=echidna and ships it as the root
// experience. See docs/WORDBLITZ.md.
const EchidnaGame = lazy(() => import('./components/echidna/EchidnaGame'));

// Admin dashboard — lazy so its 58 source modules stay out of the main
// bundle every pokerblitzgame.com visitor downloads. Route-branch lazy
// mount (not setState-triggered), so no startTransition needed — the
// PR #179 React #426 pattern only applies to synchronous-input mounts.
const Admin = lazy(() => import('./admin/Admin').then((m) => ({ default: m.Admin })));

// Game shell — lazy so the marketing landing page at / doesn't download
// (or modulepreload) the full game (globals + game-logic + supabase +
// ui-components). Native is unaffected: game-main.tsx keeps its own
// static import of ./app/page. Landing visitors who do head to /play get
// the chunk idle-prefetched below, so the Suspense gap is rarely seen.
const Home = lazy(() => import('./app/page'));

// Labs + Battle also pull the game stack (GameBoard, game-logic, theme
// contexts) — keep them out of the landing page's static graph too.
const LabsPage = lazy(() =>
  import('./components/labs/LabsPage').then((m) => ({ default: m.LabsPage })),
);
const BattlePage = lazy(() =>
  import('./components/battle/BattlePage').then((m) => ({ default: m.BattlePage })),
);

/**
 * Is this binary the standalone ECHIDNA build?
 *
 * Resolved once at module scope — the variant cannot change at runtime, and
 * this decides the root route, so it must not depend on render order.
 */
const IS_ECHIDNA_BUILD = getBrand().key === 'echidna';

function App() {
  // Track pathname in state + listen for popstate so client-side
  // pushState navigation triggers a re-render. Without this the App
  // routing decision (admin vs landing vs home) is frozen at first
  // render, and the inner <Admin /> popstate listener can re-render
  // its sub-routes but not the outer routing branch.
  // (StackedStreak-5c71)
  const [path, setPath] = useState(() => window.location.pathname);
  useEffect(() => {
    const sync = () => setPath(window.location.pathname);
    window.addEventListener('popstate', sync);
    return () => window.removeEventListener('popstate', sync);
  }, []);

  // Return-to redirect (StackedStreak-4yzw follow-up): gated surfaces that
  // have no sign-in UI of their own — the admin panel especially — send
  // unauthenticated users to `/play?returnTo=<path>` to authenticate via the
  // game's auth modal. Once a session exists, bounce them back to where they
  // came from instead of stranding them in the game (which read as "it just
  // dumps me on the main site"). Guarded to same-origin relative paths to
  // avoid an open-redirect.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('returnTo');
    // Only same-origin absolute paths ("/admin"), never "//evil.com" or
    // "https://…". Reject anything that isn't a lone-slash-prefixed path.
    if (!raw || !raw.startsWith('/') || raw.startsWith('//')) return;
    const returnTo = raw;

    let done = false;
    const go = () => {
      if (done) return;
      done = true;
      window.location.href = returnTo;
    };

    // Already signed in (came back with a live session) → go immediately.
    void supabase.auth.getSession().then(({ data }) => {
      if (data.session) go();
    });
    // Otherwise wait for the sign-in that happens on this /play visit.
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (session && (event === 'SIGNED_IN' || event === 'INITIAL_SESSION')) go();
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  // Root-path routing for visitors who land on /:
  //   • Native Capacitor app  → /play (user is already in the installed app)
  //   • iOS/Android web       → App Store / Play Store (mirrors middleware.ts;
  //                              client-side fallback for users on stale
  //                              cached HTML from before the middleware shipped
  //                              — bd StackedStreak-hm3t)
  //   • Mobile-mirror preview → /play (?mobile=1 + beta/admin flag)
  //   • Desktop / ?web=1      → fall through to <LandingPage />
  //
  // The server-side middleware (middleware.ts) is the primary route for
  // store redirects on fresh requests; this client-side block handles
  // (a) native shells, (b) the mobile-mirror preview, and (c) the cached-
  // HTML edge case where Vercel never saw the request.
  useEffect(() => {
    // ── Echidna guard, and it must be INSIDE the effect ──
    //
    // The IS_ECHIDNA_BUILD early return further down sits after all hooks so
    // the rules of hooks hold — which means it guards RENDERING only. Every
    // effect in this component still runs on the Echidna build, and this one
    // redirects any mobile user-agent to the POKER BLITZ App Store. Reported
    // from a real phone: Echidna painted for about a second, then the store
    // page replaced it. Desktop testing never caught it because the redirect
    // is UA-gated, and a resized desktop viewport keeps a desktop UA.
    //
    // On the Echidna domain the game is the product and it has no published
    // store listing yet, so there is nothing to redirect anyone to.
    if (IS_ECHIDNA_BUILD) return;
    if (path !== '/' && path !== '') return;

    // ?web=1 escape hatch (matches middleware) — show the landing page.
    const params = new URLSearchParams(window.location.search);
    if (params.get('web') === '1') return;

    // Native app or mobile-mirror preview → in-app /play (game shell).
    if (Capacitor.isNativePlatform() || isMobileLike()) {
      // Pure UA-based store routing for the web cases. `isMobileLike()`
      // also fires on a desktop browser resized below 768px wide — we
      // don't want to redirect those users to a store, so check UA
      // first and only fall through to /play for native or beta mirror.
      const ua = (typeof navigator !== 'undefined' ? navigator.userAgent : '').toLowerCase();
      if (!Capacitor.isNativePlatform()) {
        if (/iphone|ipad|ipod/.test(ua)) {
          window.location.replace(
            'https://apps.apple.com/us/app/poker-blitz/id6754580452' +
              '?utm_source=pokerblitzgame_com&utm_medium=client_fallback&utm_campaign=root_device_redirect',
          );
          return;
        }
        if (/android/.test(ua)) {
          window.location.replace(
            'https://play.google.com/store/apps/details?id=com.pokerblitz.app' +
              '&utm_source=pokerblitzgame_com&utm_medium=client_fallback&utm_campaign=root_device_redirect',
          );
          return;
        }
      }
      // Native shell, or mobile-mirror preview on desktop (small viewport,
      // no mobile UA) → in-app game shell at /play.
      const search = Capacitor.isNativePlatform() ? '' : window.location.search;
      window.location.href = `/play${search}`;
    }
  }, [path]);

  // Handle OAuth callback when app becomes active
  const handleAppFocus = () => {
    // Check if we have a session after app becomes active
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) {
        // Session is active
      }
    });
  };

  // Handle OAuth callback from URL hash (web only)
  const handleOAuthCallback = () => {
    const hash = window.location.hash;
    if (hash.includes('access_token=')) {
      // Parse the hash and handle the session
      supabase.auth.getSession().then(({ data }) => {
        if (data.session) {
          // Clear the hash from URL
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      });
    }
  };

  useEffect(() => {
    // Listen for app focus events
    window.addEventListener('focus', handleAppFocus);

    // Listen for visibility change (when app becomes active)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Check for OAuth callback on visibility change
        handleOAuthCallback();
        handleAppFocus();
        // Nudge audio in case the OS or browser suspended the AudioContext
        // while we were hidden. We don't pause on hidden anymore — iOS
        // UIBackgroundModes "audio" + AVAudioSession .playback is supposed
        // to keep music playing through brief backgrounding.
        // Dynamic import keeps AudioManager (bundled in the game-logic
        // chunk) out of the landing page's static graph; once the game
        // chunk has loaded this resolves instantly to the same singleton,
        // and before that resumeMusic() is a no-op (nothing has played).
        void import('./lib/audio/AudioManager').then(({ audioManager }) => {
          audioManager.resumeMusic();
        });
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Check for OAuth callback immediately
    handleOAuthCallback();

    // Check for session immediately
    handleAppFocus();

    return () => {
      window.removeEventListener('focus', handleAppFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Idle-prefetch the lazy game chunk while the visitor reads the landing
  // page, so a click-through to /play doesn't sit on the Suspense fallback
  // (same requestIdleCallback pattern as the lazy-modal prefetch in
  // page.tsx, PR #177/#179).
  useEffect(() => {
    // Same trap as the store redirect above: the Echidna early return guards
    // rendering, not effects. Without this, every Echidna visit prefetched the
    // entire Poker Blitz game chunk — a quarter-megabyte of the wrong game.
    if (IS_ECHIDNA_BUILD) return;
    if (path !== '/' && path !== '') return;
    const prefetchGameChunk = () => {
      void import('./app/page');
    };
    const win = window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number;
    };
    if (typeof win.requestIdleCallback === 'function') {
      win.requestIdleCallback(prefetchGameChunk, { timeout: 4000 });
    } else {
      // Safari has no requestIdleCallback; fall back to a short setTimeout
      // that runs after first paint.
      setTimeout(prefetchGameChunk, 1500);
    }
  }, [path]);
  // ── Standalone Echidna build: the whole site IS the word game ──
  //
  // Placed AFTER every hook in this component, not before them. An early
  // return above a useEffect makes that hook conditional — it only happens to
  // work here because IS_ECHIDNA_BUILD is constant for the bundle's lifetime,
  // so the order never actually varies at runtime. That is a fragile reason to
  // rely on and rules-of-hooks flags it correctly.
  //
  // Deliberately BEFORE the isWebPlayAllowed() gate below. That gate stops the
  // public stumbling into an experimental route on pokerblitzgame.com; on a
  // dedicated Echidna deploy the game is the entire point, and gating it would
  // show beta testers a "download the app" wall instead of the game.

  if (IS_ECHIDNA_BUILD) {
    return (
      <Suspense fallback={<Suspense fallback={null}><EchidnaLoader /></Suspense>}>
        <EchidnaGame />
      </Suspense>
    );
  }


  // Admin panel
  if (path.startsWith('/admin')) {
    return (
      <Suspense fallback={null}>
        <Admin />
      </Suspense>
    );
  }

  // Poker Blitz Battle — public shareable challenge landing at /b/<code>.
  // No web-play gate here: the landing is meant to be shared/viewed by anyone;
  // its "Accept Challenge" CTA routes to /play, which applies the gate.
  if (path.startsWith('/b/')) {
    const code = path.slice(3).replace(/\/+$/, '');
    return (
      <DemoModeProvider>
        <Suspense fallback={null}>
          <BattlePage code={code} />
        </Suspense>
      </DemoModeProvider>
    );
  }

  // Echidna — the word-game variant, playable on web at /echidna.
  // Self-contained (own timer/score/state), so it mounts directly without
  // the poker game shell.
  //
  // Gated by its OWN check, not by isWebPlayAllowed(). Sharing poker's gate is
  // what put this pre-beta game on the public production domain for five weeks
  // (bd StackedStreak-6xm1) — /play and /labs were deliberately ungated for the
  // Battle acquisition funnel in #261, and Echidna came along uninvited.
  // Internal testers still use the ?play=1 QA override; it now grants Echidna
  // access under Echidna's own key.
  if (path.startsWith('/echidna')) {
    if (!isEchidnaWebAccessAllowed()) return <DownloadToPlayGate />;
    return (
      <Suspense fallback={<FullScreenLoader />}>
        <EchidnaGame />
      </Suspense>
    );
  }

  // Labs — experimental game modes. `/labs?mode=rush` drops straight into
  // Rush; bare `/labs` still shows the chooser for QA. The legacy chooser
  // is also reachable for backwards compat with shared links.
  if (path.startsWith('/labs')) {
    // Web play is gated for the public — see isWebPlayAllowed().
    if (!isWebPlayAllowed()) return <DownloadToPlayGate />;
    const params = new URLSearchParams(window.location.search);
    const mode = params.get('mode');
    const initialMode =
      mode === 'rush' || mode === 'classic' || mode === 'dictated' ? mode : undefined;
    return (
      <Suspense fallback={null}>
        <LabsPage initialMode={initialMode} />
      </Suspense>
    );
  }

  // Landing page at root - this entry point is for web only
  // Native apps will be redirected to /play above
  if (path === '/' || path === '') {
    return (
      <DemoModeProvider>
        <LandingPage />
      </DemoModeProvider>
    );
  }

  // /play path or other routes - show the game.
  // Web play is gated for the public — the playable web shell isn't
  // production-ready, so steer visitors to the native apps instead.
  // Native, dev, smoke, and the ?play=1 QA override bypass this.
  if (!isWebPlayAllowed()) {
    return <DownloadToPlayGate />;
  }

  return (
    <DemoModeProvider>
      {/* InstallPrompt moved into the menu (page.tsx) so it never overlaps the
          game board during play. */}
      <Suspense fallback={null}>
        <Home />
      </Suspense>
    </DemoModeProvider>
  );
}

export default App;
